import { useMemo, useState } from "react";
import api from "@api";
import "./TournamentBracketSettings.css";

function TournamentBracketSettings({ id, pageId, page }) {
 const existingContent = page?.pageSettings?.content || {};

 const [bracketTitle, setBracketTitle] = useState(
  existingContent.bracket_title || "Tournament Bracket"
 );

 const [backgroundImageFile, setBackgroundImageFile] =
  useState(null);

 const [existingBackgroundImageUrl, setExistingBackgroundImageUrl] =
  useState(existingContent.background_image_url || "");
  const [backgroundColor, setBackgroundColor] = useState(
    existingContent.background_color || "#111111"
  );

  const [titleColor, setTitleColor] = useState(
    existingContent.title_color || "#ffffff"
  );
 const [removeBackgroundImage, setRemoveBackgroundImage] =
  useState(false);

 const [saving, setSaving] = useState(false);

 const previewImageUrl = useMemo(() => {
  if (backgroundImageFile) {
   return URL.createObjectURL(backgroundImageFile);
  }

  if (!removeBackgroundImage) {
   return existingBackgroundImageUrl;
  }

  return "";
 }, [
  backgroundImageFile,
  existingBackgroundImageUrl,
  removeBackgroundImage,
 ]);

 const handleImageChange = (event) => {
  const file = event.target.files?.[0] || null;

  setBackgroundImageFile(file);

  if (file) {
   setRemoveBackgroundImage(false);
  }
 };

 const handleRemoveImage = () => {
  setBackgroundImageFile(null);
  setRemoveBackgroundImage(true);
 };

 const handleUndoRemoveImage = () => {
  setRemoveBackgroundImage(false);
 };

 const handleSave = async () => {
  setSaving(true);

  try {
   const formData = new FormData();

    const nextContent = {
      ...existingContent,
      bracket_title:
        bracketTitle.trim() || "Tournament Bracket",
      background_image_url:
        removeBackgroundImage
          ? ""
          : existingBackgroundImageUrl,
      background_color: backgroundColor,
      title_color: titleColor,
    };

   if (backgroundImageFile) {
    formData.append(
     "background_image_url",
     backgroundImageFile
    );
   }

   formData.append(
    "content",
    JSON.stringify(nextContent)
   );

   formData.append(
    "imageContentKeys",
    JSON.stringify(["background_image_url"])
   );

   formData.append(
    "removedImageKeys",
    JSON.stringify(
     removeBackgroundImage
      ? ["background_image_url"]
      : []
    )
   );

   await api.put(
    `/api/organization-page/${id}/${pageId}`,
    formData,
    {
     headers: {
      "Content-Type": "multipart/form-data",
     },
    }
   );

   if (removeBackgroundImage) {
    setExistingBackgroundImageUrl("");
   }

   setBackgroundImageFile(null);
   setRemoveBackgroundImage(false);

   alert("Tournament bracket settings saved.");
  } catch (err) {
   console.error(
    "Failed to save tournament bracket settings:",
    err
   );

   alert("Could not save tournament bracket settings.");
  } finally {
   setSaving(false);
  }
 };

 return (
  <div className="tournament-bracket-settings">
   <div className="tournament-bracket-settings-card">
    <div className="tournament-bracket-settings-header">
     <h2 className="tournament-bracket-settings-title">
      Tournament Bracket Settings
     </h2>

     <p className="tournament-bracket-settings-subtext">
      Customize the title and background image shown on the
      public tournament bracket page.
     </p>
    </div>

    <div className="tournament-bracket-settings-group">
     <label className="tournament-bracket-settings-field">
      <span className="tournament-bracket-settings-label">
       Bracket Title
      </span>

      <input
       type="text"
       value={bracketTitle}
       onChange={(event) =>
        setBracketTitle(event.target.value)
       }
       placeholder="Tournament Bracket"
      />
     </label>
    </div>
       <div className="tournament-bracket-settings-color-grid">

         <label className="tournament-bracket-settings-field">
           <span className="tournament-bracket-settings-label">
             Title Color
           </span>

           <input
             type="color"
             value={titleColor}
             onChange={(event) =>
               setTitleColor(event.target.value)
             }
           />
         </label>
         <label className="tournament-bracket-settings-field">
           <span className="tournament-bracket-settings-label">
             Background Color
           </span>

           <input
             type="color"
             value={backgroundColor}
             onChange={(event) =>
               setBackgroundColor(event.target.value)
             }
           />
         </label>

       </div>
    <div className="tournament-bracket-settings-group">
     <h3 className="tournament-bracket-settings-group-title">
      Background Image
     </h3>

     <p className="tournament-bracket-settings-group-subtext">
      Upload an image to display behind the public bracket.
     </p>

     {previewImageUrl ? (
      <div className="tournament-bracket-settings-preview">
       <img
        src={previewImageUrl}
        alt="Tournament bracket background preview"
       />
      </div>
     ) : (
      <div className="tournament-bracket-settings-empty-preview">
       No background image selected.
      </div>
     )}

     {existingBackgroundImageUrl &&
      !backgroundImageFile &&
      !removeBackgroundImage ? (
      <div className="tournament-bracket-settings-existing-row">
       <span>Current background image uploaded</span>

       <a
        href={existingBackgroundImageUrl}
        target="_blank"
        rel="noreferrer"
       >
        View
       </a>

       <button
        type="button"
        onClick={handleRemoveImage}
       >
        Remove
       </button>
      </div>
     ) : null}

     {removeBackgroundImage ? (
      <div className="tournament-bracket-settings-existing-row">
       <span>
        Background image will be removed when saved.
       </span>

       <button
        type="button"
        onClick={handleUndoRemoveImage}
       >
        Undo
       </button>
      </div>
     ) : null}

     <div className="tournament-bracket-settings-file-row">
      <input
       id="tournament-bracket-background-upload"
       type="file"
       accept="image/*"
       onChange={handleImageChange}
      />

      {backgroundImageFile ? (
       <span>{backgroundImageFile.name}</span>
      ) : null}
     </div>
    </div>

    <div className="tournament-bracket-settings-actions">
     <button
      type="button"
      className="tournament-bracket-settings-save-btn"
      onClick={handleSave}
      disabled={saving}
     >
      {saving ? "Saving..." : "Save Settings"}
     </button>
    </div>
   </div>
  </div>
 );
}

export default TournamentBracketSettings;